import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Briefcase, 
  Plus, 
  MapPin, 
  Building2, 
  Truck, 
  Edit, 
  Trash2, 
  Search,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Clock,
  DollarSign,
  AlertTriangle,
  X,
  Save,
  Filter,
  Settings
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '../components/ui/sheet';
import { ScrollArea } from '../components/ui/scroll-area';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';

const API = axios.create({
  baseURL: process.env.REACT_APP_BACKEND_URL + '/api',
});

API.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const siteTypes = ['CBD', 'Metropolitan', 'Regional', 'Remote'];
const buildingTypes = ['Multi-storey', 'Single-storey', 'Industrial', 'Commercial', 'Residential'];

const SitesPage = () => {
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [editingSite, setEditingSite] = useState(null);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [expandedSite, setExpandedSite] = useState(null);
  const [ruleOptions, setRuleOptions] = useState([]);
  const [equipmentTypeOptions, setEquipmentTypeOptions] = useState([]);
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    job_number: '',
    job_name: '',
    client_name: '',
    site_address: '',
    site_type: 'Metropolitan',
    building_type: '',
    building_height_floors: '',
    equipment_types: [],
    travel_distance_km: '',
    site_allowances: [],
    special_conditions: '',
    start_date: '',
    end_date: ''
  });

  useEffect(() => {
    fetchSites();
    fetchRulesFromEngine();
    fetchEquipmentTypes();
  }, []);

  const fetchSites = async () => {
    setLoading(true);
    try {
      const response = await API.get('/jobs');
      setSites(response.data);
    } catch (error) {
      toast.error('Failed to load sites');
    } finally {
      setLoading(false);
    }
  };

  const fetchEquipmentTypes = async () => {
    try {
      const response = await API.get('/equipment-types');
      setEquipmentTypeOptions(response.data);
    } catch (error) {
      console.error('Failed to fetch equipment types:', error);
      setEquipmentTypeOptions([]);
    }
  };

  const fetchRulesFromEngine = async () => {
    try {
      const response = await API.get('/rules');
      const rules = response.data;
      
      // Filter to only enabled rules and format as "Source Section - Category"
      const ruleList = rules
        .filter(rule => rule.is_active !== false) // Only enabled rules
        .map(rule => ({
          id: rule.rule_id || rule.id,
          label: `${rule.source_section || 'Custom Rules'} - ${rule.category}`,
          category: rule.category,
          source_section: rule.source_section || 'Custom Rules'
        }))
        .sort((a, b) => a.label.localeCompare(b.label));
      
      // Remove duplicates based on label
      const uniqueRules = ruleList.filter((rule, index, self) => 
        index === self.findIndex(r => r.label === rule.label)
      );
      
      setRuleOptions(uniqueRules);
    } catch (error) {
      console.error('Failed to fetch rules:', error);
      setRuleOptions([]);
    }
  };

  const resetForm = () => {
    setFormData({
      job_number: '',
      job_name: '',
      client_name: '',
      site_address: '',
      site_type: 'Metropolitan',
      building_type: '',
      building_height_floors: '',
      equipment_types: [],
      travel_distance_km: '',
      site_allowances: [],
      special_conditions: '',
      start_date: '',
      end_date: ''
    });
    setEditingSite(null);
  };

  const handleEdit = (site) => {
    setEditingSite(site);
    setFormData({
      job_number: site.job_number || '',
      job_name: site.job_name || '',
      client_name: site.client_name || '',
      site_address: site.site_address || '',
      site_type: site.site_type || 'Metropolitan',
      building_type: site.building_type || '',
      building_height_floors: site.building_height_floors || '',
      equipment_types: site.equipment_types || [],
      travel_distance_km: site.travel_distance_km || '',
      site_allowances: site.site_allowances || [],
      special_conditions: site.special_conditions || '',
      start_date: site.start_date || '',
      end_date: site.end_date || ''
    });
    setIsSheetOpen(true);
  };

  const handleSave = async () => {
    if (!formData.job_number || !formData.job_name || !formData.client_name) {
      toast.error('Please fill in required fields');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        building_height_floors: formData.building_height_floors ? parseInt(formData.building_height_floors) : null,
        travel_distance_km: formData.travel_distance_km ? parseFloat(formData.travel_distance_km) : null
      };

      if (editingSite) {
        await API.put(`/jobs/${editingSite.id}`, payload);
        toast.success('Site updated successfully');
      } else {
        await API.post('/jobs', payload);
        toast.success('Site created successfully');
      }
      setIsSheetOpen(false);
      resetForm();
      fetchSites();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save site');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (siteId) => {
    try {
      await API.delete(`/jobs/${siteId}`);
      toast.success('Site deleted successfully');
      fetchSites();
    } catch (error) {
      toast.error('Failed to delete site');
    }
  };

  const toggleEquipmentType = (type) => {
    setFormData(prev => ({
      ...prev,
      equipment_types: prev.equipment_types.includes(type)
        ? prev.equipment_types.filter(t => t !== type)
        : [...prev.equipment_types, type]
    }));
  };

  const toggleRule = (rule) => {
    setFormData(prev => ({
      ...prev,
      site_allowances: prev.site_allowances.includes(rule)
        ? prev.site_allowances.filter(r => r !== rule)
        : [...prev.site_allowances, rule]
    }));
  };

  const filteredSites = sites.filter(site => {
    const matchesSearch = site.job_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          site.job_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          site.client_name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || site.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-700 border-green-200';
      case 'completed': return 'bg-slate-100 text-slate-700 border-slate-200';
      case 'on-hold': return 'bg-amber-100 text-amber-700 border-amber-200';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600">
              <Building2 className="w-5 h-5 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900">Sites & Projects</h1>
          </div>
          <p className="text-slate-600 mt-1 ml-12">Manage sites, equipment assignments, and applicable rules</p>
        </div>
        <Button onClick={() => { resetForm(); setIsSheetOpen(true); }} className="gap-2">
          <Plus className="w-4 h-4" />
          Add Site
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input 
                placeholder="Search sites..." 
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="on-hold">On Hold</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={fetchSites} className="gap-2">
              <RefreshCw className="w-4 h-4" />
              Refresh
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Sites List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="w-6 h-6 animate-spin text-primary" />
        </div>
      ) : filteredSites.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">No sites found</p>
            <Button className="mt-4" onClick={() => { resetForm(); setIsSheetOpen(true); }}>
              <Plus className="w-4 h-4 mr-2" />
              Create First Site
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filteredSites.map((site) => (
            <Card key={site.id} className="overflow-hidden">
              <div 
                className="p-4 cursor-pointer hover:bg-slate-50 transition-colors"
                onClick={() => setExpandedSite(expandedSite === site.id ? null : site.id)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="p-2 rounded-lg bg-blue-50">
                      <Building2 className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-sm text-slate-500">{site.job_number}</span>
                        <h3 className="font-semibold text-slate-900">{site.job_name}</h3>
                      </div>
                      <div className="flex items-center gap-4 mt-1 text-sm text-slate-500">
                        <span>{site.client_name}</span>
                        <span className="flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          {site.site_type}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge className={getStatusColor(site.status)}>
                      {site.status}
                    </Badge>
                    {site.site_allowances?.length > 0 && (
                      <Badge variant="secondary">
                        {site.site_allowances.length} rules
                      </Badge>
                    )}
                    {expandedSite === site.id ? (
                      <ChevronUp className="w-5 h-5 text-slate-400" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-slate-400" />
                    )}
                  </div>
                </div>
              </div>
              
              {expandedSite === site.id && (
                <div className="border-t border-slate-100 bg-slate-50/50 p-4">
                  <div className="grid grid-cols-3 gap-6">
                    <div>
                      <Label className="text-xs text-slate-500 uppercase">Site Details</Label>
                      <p className="text-sm mt-1">{site.site_address}</p>
                      {site.building_type && (
                        <p className="text-sm text-slate-600">{site.building_type} {site.building_height_floors ? `(${site.building_height_floors} floors)` : ''}</p>
                      )}
                      {site.travel_distance_km && (
                        <p className="text-sm text-slate-600 flex items-center gap-1 mt-1">
                          <Truck className="w-3 h-3" />
                          {site.travel_distance_km} km travel
                        </p>
                      )}
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500 uppercase">Equipment</Label>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {site.equipment_types?.map((eq, i) => (
                          <Badge key={i} variant="outline" className="text-xs">{eq}</Badge>
                        ))}
                        {(!site.equipment_types || site.equipment_types.length === 0) && (
                          <span className="text-sm text-slate-400">Not specified</span>
                        )}
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500 uppercase">Rules Applicable</Label>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {site.site_allowances?.map((rule, i) => (
                          <Badge key={i} className="text-xs bg-green-100 text-green-700">{rule}</Badge>
                        ))}
                        {(!site.site_allowances || site.site_allowances.length === 0) && (
                          <span className="text-sm text-slate-400">None</span>
                        )}
                      </div>
                    </div>
                  </div>
                  {site.special_conditions && (
                    <div className="mt-4 p-3 bg-amber-50 rounded-lg border border-amber-200">
                      <Label className="text-xs text-amber-700 uppercase flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" />
                        Special Conditions
                      </Label>
                      <p className="text-sm text-amber-800 mt-1">{site.special_conditions}</p>
                    </div>
                  )}
                  <div className="flex justify-end gap-2 mt-4">
                    <Button variant="outline" size="sm" onClick={() => handleEdit(site)}>
                      <Edit className="w-3 h-3 mr-1" />
                      Edit
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="outline" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50">
                          <Trash2 className="w-3 h-3 mr-1" />
                          Delete
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete Site?</AlertDialogTitle>
                          <AlertDialogDescription>
                            This will permanently delete &quot;{site.job_name}&quot;. This action cannot be undone.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={() => handleDelete(site.id)} className="bg-red-600 hover:bg-red-700">
                            Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              )}
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Sheet */}
      <Sheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
        <SheetContent>
          <SheetHeader>
            <SheetTitle>{editingSite ? 'Edit Site' : 'Create New Site'}</SheetTitle>
            <SheetDescription>
              {editingSite ? 'Update site details and applicable rules' : 'Add a new site/project with location information'}
            </SheetDescription>
          </SheetHeader>

          <ScrollArea className="h-[calc(100vh-180px)] pr-4 mt-6">
            <div className="space-y-6">
              {/* Basic Info */}
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Site Number *</Label>
                    <Input 
                      placeholder="SITE-2024-001"
                      value={formData.job_number}
                      onChange={(e) => setFormData({...formData, job_number: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Status</Label>
                    <Select 
                      value={editingSite?.status || 'active'} 
                      onValueChange={(value) => setFormData({...formData, status: value})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="completed">Completed</SelectItem>
                        <SelectItem value="on-hold">On Hold</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>Site Name *</Label>
                  <Input 
                    placeholder="Crown Tower Development"
                    value={formData.job_name}
                    onChange={(e) => setFormData({...formData, job_name: e.target.value})}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Client Name *</Label>
                  <Input 
                    placeholder="Crown Construction"
                    value={formData.client_name}
                    onChange={(e) => setFormData({...formData, client_name: e.target.value})}
                  />
                </div>
              </div>

              {/* Site Details */}
              <div className="border-t pt-4">
                <Label className="text-xs uppercase tracking-wider text-slate-500 mb-3 block">Site Details</Label>
                
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Site Address</Label>
                    <Input 
                      placeholder="123 Collins Street, Melbourne"
                      value={formData.site_address}
                      onChange={(e) => setFormData({...formData, site_address: e.target.value})}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Site Type</Label>
                      <Select value={formData.site_type} onValueChange={(value) => setFormData({...formData, site_type: value})}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {siteTypes.map(type => (
                            <SelectItem key={type} value={type}>{type}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Building Type</Label>
                      <Select value={formData.building_type || ''} onValueChange={(value) => setFormData({...formData, building_type: value})}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select..." />
                        </SelectTrigger>
                        <SelectContent>
                          {buildingTypes.map(type => (
                            <SelectItem key={type} value={type}>{type}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Building Height (Floors)</Label>
                      <Input 
                        type="number"
                        placeholder="15"
                        value={formData.building_height_floors}
                        onChange={(e) => setFormData({...formData, building_height_floors: e.target.value})}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Travel Distance (km)</Label>
                      <Input 
                        type="number"
                        step="0.1"
                        placeholder="15.5"
                        value={formData.travel_distance_km}
                        onChange={(e) => setFormData({...formData, travel_distance_km: e.target.value})}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Equipment Types */}
              <div className="border-t pt-4">
                <Label className="text-xs uppercase tracking-wider text-slate-500 mb-3 block">Equipment Types</Label>
                {equipmentTypeOptions.length === 0 ? (
                  <div className="text-sm text-slate-500 p-3 bg-slate-50 rounded-lg border border-dashed">
                    No equipment types defined in Settings. 
                    <a href="/settings" className="text-primary ml-1 hover:underline">Add equipment types →</a>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-2">
                    {equipmentTypeOptions.map(equip => (
                      <div key={equip.id} className="flex items-center space-x-2">
                        <Checkbox 
                          id={`eq-${equip.id}`}
                          checked={formData.equipment_types.includes(equip.name)}
                          onCheckedChange={() => toggleEquipmentType(equip.name)}
                        />
                        <label htmlFor={`eq-${equip.id}`} className="text-sm cursor-pointer">{equip.name}</label>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Rules Applicable */}
              <div className="border-t pt-4">
                <div className="flex items-center justify-between mb-3">
                  <Label className="text-xs uppercase tracking-wider text-slate-500 flex items-center gap-1">
                    <Settings className="w-3 h-3" />
                    Rules Applicable
                  </Label>
                  <span className="text-xs text-slate-400">Enabled rules only</span>
                </div>
                {ruleOptions.length === 0 ? (
                  <div className="text-sm text-slate-500 p-3 bg-slate-50 rounded-lg border border-dashed">
                    No enabled rules in Rules Engine. 
                    <a href="/rules" className="text-primary ml-1 hover:underline">Add rules →</a>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {ruleOptions.map(rule => (
                      <div key={rule.id} className="flex items-center space-x-2">
                        <Checkbox 
                          id={`rule-${rule.id}`}
                          checked={formData.site_allowances.includes(rule.label)}
                          onCheckedChange={() => toggleRule(rule.label)}
                        />
                        <label htmlFor={`rule-${rule.id}`} className="text-sm cursor-pointer truncate flex-1" title={rule.label}>{rule.label}</label>
                      </div>
                    ))}
                  </div>
                )}
                {formData.site_allowances.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-1">
                    {formData.site_allowances.map(rule => (
                      <Badge key={rule} variant="secondary" className="text-xs">
                        {rule}
                        <button 
                          className="ml-1 hover:text-red-600" 
                          onClick={() => toggleRule(rule)}
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
              </div>

              {/* Special Conditions */}
              <div className="border-t pt-4">
                <div className="space-y-2">
                  <Label>Special Conditions</Label>
                  <Textarea 
                    placeholder="Any special site conditions or notes..."
                    value={formData.special_conditions}
                    onChange={(e) => setFormData({...formData, special_conditions: e.target.value})}
                    rows={3}
                  />
                </div>
              </div>

              {/* Dates */}
              <div className="border-t pt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Start Date</Label>
                    <Input 
                      type="date"
                      value={formData.start_date}
                      onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>End Date</Label>
                    <Input 
                      type="date"
                      value={formData.end_date}
                      onChange={(e) => setFormData({...formData, end_date: e.target.value})}
                    />
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4 border-t">
                <Button className="flex-1" onClick={handleSave} disabled={saving}>
                  {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  {editingSite ? 'Update Site' : 'Create Site'}
                </Button>
                <Button variant="outline" onClick={() => { setIsSheetOpen(false); resetForm(); }}>
                  <X className="w-4 h-4 mr-2" />
                  Cancel
                </Button>
              </div>
            </div>
          </ScrollArea>
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default SitesPage;
